import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import type {
  Agreement,
  AgreementImplFeeTier,
  AgreementTerm,
  AgreementTermWithTiers,
  DealStatementPrepaid,
  Partner,
  PartnerRollupRow,
  ReportScope,
} from "@crm/shared";
import styles from "./PartnerDetailPage.module.css";
import { Badge } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardBody, CardHeader } from "../../design/Card.js";
import { EmptyState, ProblemMessage, Spinner } from "../../design/Feedback.js";
import { FxFootnote } from "../../design/FxFootnote.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { StatTile, StatTileRow } from "../../design/StatTile.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { formatCount, formatMoney, formatPercent } from "../../format/money.js";
import { DeleteDialog } from "../../forms/DeleteDialog.js";
import {
  useAgreementImplFeeTierMutations,
  useAgreementMutations,
  useAgreementTermMutations,
  usePartnerMutations,
} from "../../api/mutations.js";
import { BillingContactsCard } from "./BillingContactsCard.js";
import { PrepaidEntitlementsSection } from "../deals/PrepaidEntitlementsSection.js";
import { useHasRole } from "../../auth/useAuth.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { AgreementFormDialog } from "./AgreementFormDialog.js";
import { AgreementImplFeeTierFormDialog } from "./AgreementImplFeeTierFormDialog.js";
import { AgreementTermFormDialog } from "./AgreementTermFormDialog.js";
import { AgreementTermsTimeline } from "./AgreementTermsTimeline.js";
import { PartnerFormDialog } from "./PartnerFormDialog.js";
import {
  useAgreementDetails,
  useAgreements,
  useChildPartners,
  usePartner,
  usePartnerRollupReport,
} from "../../api/queries.js";

/**
 * The two questions a partner that owns other companies can be asked, and the
 * reason the toggle only appears for one: for a partner with nothing beneath
 * it the two scopes return the same figures, and a control that cannot change
 * the answer is worse than no control at all.
 */
/**
 * A partner page reads no statement, so it has no pool positions to show — a
 * pool bought on one deal may cover clauses on several, and there is no single
 * period to evaluate it against. Named rather than written inline, so the
 * emptiness reads as the decision it is.
 */
const NO_POSITIONS: ReadonlyMap<string, DealStatementPrepaid> = new Map();

const SCOPES: ReadonlyArray<SegmentedOption<ReportScope>> = [
  { value: "direct", label: "This company" },
  { value: "rolled_up", label: "Including owned companies" },
];

/** Every dialog this screen can open, as one value — two can never be open at once. */
type PartnerScreenAction =
  | { kind: "edit_partner"; partner: Partner }
  | { kind: "delete_partner"; partner: Partner }
  | { kind: "create_agreement" }
  | { kind: "edit_agreement"; agreement: Agreement }
  | { kind: "delete_agreement"; agreement: Agreement }
  | { kind: "create_term"; agreement: Agreement }
  | { kind: "edit_term"; agreement: Agreement; term: AgreementTerm }
  | { kind: "delete_term"; agreement: Agreement; term: AgreementTerm }
  // The band dialogs carry the TERM as well as the tier, because a band has no
  // currency or effective date of its own to render — both come from the term
  // that priced it.
  | { kind: "create_tier"; term: AgreementTermWithTiers }
  | { kind: "edit_tier"; term: AgreementTermWithTiers; tier: AgreementImplFeeTier }
  | { kind: "delete_tier"; term: AgreementTermWithTiers; tier: AgreementImplFeeTier };

export function PartnerDetailPage(): ReactNode {
  const navigate = useNavigate();
  const { partnerId } = useParams({ from: "/partners/$partnerId" });
  const partner = usePartner(partnerId);
  const agreements = useAgreements(partnerId);
  const canEdit = useHasRole("crm_editor");
  const [action, setAction] = useState<PartnerScreenAction | null>(null);

  const partnerMutations = usePartnerMutations();
  const agreementMutations = useAgreementMutations();
  const termMutations = useAgreementTermMutations();
  const tierMutations = useAgreementImplFeeTierMutations();
  const partnerName = partner.data?.name ?? "this partner";
  const record = partner.data;

  // The record IS the title, so the bar says nothing until the record arrives —
  // a placeholder name would be a claim about a partner nobody has read yet.
  usePageChrome({
    title: record?.name ?? "Partner",
  });

  return (
    <div className={styles.page}>
      <div className={styles.crumbs}>
        <Link to="/partners">Partners</Link>
        <span className={styles.crumbSeparator} aria-hidden="true">
          /
        </span>
        <span className={styles.crumbCurrent}>{record?.name ?? "…"}</span>
        {canEdit && record !== undefined && (
          <div className={styles.actions}>
            <Button onClick={() => setAction({ kind: "edit_partner", partner: record })}>
              Edit partner
            </Button>
            <Button onClick={() => setAction({ kind: "delete_partner", partner: record })}>
              Delete
            </Button>
            <Button variant="primary" onClick={() => setAction({ kind: "create_agreement" })}>
              <span aria-hidden="true">+</span>
              New agreement
            </Button>
          </div>
        )}
      </div>

      {/* Carries this query's loading and failure states. Everything derived
          from the partner — who owns it, what it owns, what it is worth —
          hangs off the record, so it all lives behind this one boundary. */}
      <QueryBoundary query={partner} label="this partner">
        {(item) => <PartnerOverview partner={item} />}
      </QueryBoundary>

      <QueryBoundary query={agreements} label="this partner's agreements">
        {(page) =>
          page.items.length === 0 ? (
            <Card>
              <EmptyState
                title="No agreements"
                description="Deals under this partner have no agreement to resolve a split from yet. Add one, then give it an effective-dated term."
              />
            </Card>
          ) : (
            <AgreementCards agreements={page.items} canEdit={canEdit} onAction={setAction} />
          )
        }
      </QueryBoundary>

      {/* Below the agreements, because a pool only means something once the
          terms that split what it covers are on screen above it. A pool bought
          on one deal may cover clauses on several, which is why it is a
          partner-level card and not only a deal-level one. */}
      <BillingContactsCard
        owner={{ kind: "partner", id: partnerId }}
        ownerName={partnerName}
        canEdit={canEdit}
      />

      <PrepaidEntitlementsSection
        scope={{ kind: "partner", id: partnerId }}
        positions={NO_POSITIONS}
        ownerName={partnerName}
        currency="CAD"
      />

      {action?.kind === "edit_partner" && (
        <PartnerFormDialog partner={action.partner} onClose={() => setAction(null)} />
      )}
      {action?.kind === "delete_partner" && (
        <DeleteDialog
          open
          onClose={() => setAction(null)}
          title="Delete partner"
          description={`${action.partner.name} will be removed, along with its agreements, their terms and every deal underneath them.`}
          record={action.partner}
          remove={(id, version) => partnerMutations.remove.mutateAsync({ id, version })}
          fetchCurrent={partnerMutations.fetchCurrent}
          onDeleted={() => {
            setAction(null);
            void navigate({ to: "/partners" });
          }}
        />
      )}
      {action?.kind === "create_agreement" && (
        <AgreementFormDialog
          partnerId={partnerId}
          partnerName={partnerName}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "edit_agreement" && (
        <AgreementFormDialog
          partnerId={partnerId}
          partnerName={partnerName}
          agreement={action.agreement}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "delete_agreement" && (
        <DeleteDialog
          open
          onClose={() => setAction(null)}
          title="Delete agreement"
          description={`${action.agreement.name} and its term timeline will be removed. Deals still pointing at it cannot resolve a split.`}
          record={action.agreement}
          remove={(id, version) => agreementMutations.remove.mutateAsync({ id, version })}
          fetchCurrent={agreementMutations.fetchCurrent}
          onDeleted={() => setAction(null)}
        />
      )}
      {action?.kind === "create_term" && (
        <AgreementTermFormDialog
          agreementId={action.agreement.id}
          agreementName={action.agreement.name}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "edit_term" && (
        <AgreementTermFormDialog
          agreementId={action.agreement.id}
          agreementName={action.agreement.name}
          term={action.term}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "delete_term" && (
        <DeleteDialog
          open
          onClose={() => setAction(null)}
          title="Delete term"
          description={`The term effective ${action.term.effective_date} will be removed from ${action.agreement.name}. Deleting the only term in force leaves deals with no split to resolve.`}
          record={action.term}
          remove={(id, version) => termMutations.remove.mutateAsync({ id, version })}
          fetchCurrent={termMutations.fetchCurrent}
          onDeleted={() => setAction(null)}
        />
      )}
      {/* The band dialogs are only reachable from a term that has declared a
          fee currency (the timeline hides "Add band" otherwise), so the
          non-null assertion below is the timeline's guarantee rather than an
          assumption — but it is written as a guard, because a term whose
          currency was cleared in another tab would otherwise render a form
          quoting amounts in nothing. */}
      {action?.kind === "create_tier" && action.term.impl_fee_currency !== null && (
        <AgreementImplFeeTierFormDialog
          agreementTermId={action.term.id}
          termEffectiveDate={action.term.effective_date}
          currency={action.term.impl_fee_currency}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "edit_tier" && (
        <AgreementImplFeeTierFormDialog
          agreementTermId={action.term.id}
          termEffectiveDate={action.term.effective_date}
          currency={action.tier.currency}
          tier={action.tier}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "delete_tier" && (
        <DeleteDialog
          open
          onClose={() => setAction(null)}
          title="Delete fee band"
          description={
            action.tier.max_accounts === null
              ? "The open-ended top band will be removed. Any customer above the highest remaining ceiling will then resolve no implementation fee at all — the report reports that as no_tier_match rather than falling back to the band below."
              : `The band up to ${formatCount(action.tier.max_accounts)} accounts will be removed. Customers that size will fall into the next band up.`
          }
          record={action.tier}
          remove={(id, version) => tierMutations.remove.mutateAsync({ id, version })}
          fetchCurrent={tierMutations.fetchCurrent}
          onDeleted={() => setAction(null)}
        />
      )}
    </div>
  );
}

/**
 * Who owns this partner, what it owns, and what it is worth. Split out of the
 * page because all three depend on the loaded record — `child_count` decides
 * whether the children are worth a request at all, and hooks cannot be called
 * on a record that has not arrived.
 */
function PartnerOverview({ partner }: { partner: Partner }): ReactNode {
  const [scope, setScope] = useState<ReportScope>("rolled_up");
  const hasChildren = partner.child_count > 0;
  // A leaf partner is asked the default question and never offered the other
  // one, because for it the two are the same question.
  // Across every deal of the partner, so in the reader's report currency.
  const rollup = usePartnerRollupReport(useReportCurrency(), partner.id, scope);
  const children = useChildPartners(partner.id, hasChildren);

  // Both scopes are read the same way: the line about THIS partner. Under
  // `direct` the report also returns a line per company beneath it, so the
  // report's `totals` would answer the family question under a heading that
  // says otherwise.
  const report = rollup.data;
  const row = report?.rows.find((candidate) => candidate.partner_id === partner.id);

  return (
    <>
      {partner.parent_id !== null && partner.parent_name !== null && (
        <p className={styles.ownership}>
          Owned by{" "}
          <Link to="/partners/$partnerId" params={{ partnerId: partner.parent_id }}>
            {partner.parent_name}
          </Link>
          . Deals here fall back to its default agreement unless they pin one of their own.
        </p>
      )}

      {partner.notes !== null && <p className={styles.notes}>{partner.notes}</p>}

      <Card>
        <CardHeader
          title="Figures"
          description={rollupDescription(partner, scope, row)}
          {...(hasChildren
            ? {
                actions: (
                  <Segmented
                    label="Rollup scope"
                    variant="inset"
                    options={SCOPES}
                    value={scope}
                    onChange={setScope}
                  />
                ),
              }
            : {})}
        />
        {rollup.error !== null ? (
          <div className={styles.panelBody}>
            <ProblemMessage error={rollup.error} action="load this partner's figures" />
          </div>
        ) : report === undefined || row === undefined ? (
          <EmptyState
            title={rollup.isPending ? "Loading the figures…" : "Nothing to roll up yet"}
            description="No deal, and no billing history, under this partner in the chosen scope."
          />
        ) : (
          <>
            <CardBody>
              <StatTileRow>
                <StatTile
                  label="Open pipeline"
                  value={formatMoney(row.open_pipeline_weighted_minor, report.currency)}
                  hint={`${formatCount(row.open_deal_count)} open deal${row.open_deal_count === 1 ? "" : "s"}, weighted by win probability`}
                />
                <StatTile
                  label="Contracted recurring"
                  value={formatMoney(row.contracted_recurring_annual_minor, report.currency)}
                  hint="Annual run rate across won and live deals. One-time fees excluded."
                />
                <StatTile
                  label="Billed to date"
                  value={formatMoney(row.billed_to_date_gross_minor, report.currency)}
                  hint="Gross on items sent or paid — actual where one has been recorded."
                />
                <StatTile
                  label="Our share billed"
                  value={formatMoney(row.billed_to_date_our_share_minor, report.currency)}
                  hint={
                    row.effective_blended_split_pct === null
                      ? "Nothing billed yet, so there is no blend to report."
                      : `A blended ${formatPercent(row.effective_blended_split_pct)} of what was billed.`
                  }
                />
              </StatTileRow>
              {/* Cost sharing gets its own row rather than four more tiles in
                  the one above: a recharge is not revenue, and sitting it
                  beside "billed to date" invites reading the two as one total.
                  Hidden entirely when this family shares no costs — an all-zero
                  row would imply the arrangement exists and produced nothing. */}
              {(row.cost_recharged_to_date_minor !== 0 ||
                row.cost_absorbed_to_date_minor !== 0 ||
                row.cost_pending_recharge_minor !== 0) && (
                <StatTileRow>
                  <StatTile
                    label="Costs recharged"
                    value={formatMoney(row.cost_recharged_to_date_minor, report.currency)}
                    hint="The partner's share of shared costs, on recharges sent or paid."
                  />
                  <StatTile
                    label="Costs absorbed"
                    value={formatMoney(row.cost_absorbed_to_date_minor, report.currency)}
                    hint="Our share of those same costs — what we bore and never billed."
                  />
                  <StatTile
                    label="Not yet recharged"
                    value={formatMoney(row.cost_pending_recharge_minor, report.currency)}
                    hint="Costs recorded with no recharge raised against them. The only figure here anyone can still act on."
                  />
                </StatTileRow>
              )}
            </CardBody>
            <FxFootnote
              currency={report.currency}
              fx={report.fx}
              generatedAt={report.generated_at}
            />
          </>
        )}
      </Card>

      {hasChildren && (
        <Card>
          <CardHeader
            title="Owned companies"
            description="Their deals inherit this partner's default agreement unless they have one of their own."
          />
          <QueryBoundary query={children} label="the companies this partner owns">
            {(page) => (
              <CardBody>
                <ul className={styles.children}>
                  {page.items.map((child) => (
                    <li key={child.id}>
                      <Link to="/partners/$partnerId" params={{ partnerId: child.id }}>
                        {child.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardBody>
            )}
          </QueryBoundary>
        </Card>
      )}
    </>
  );
}

/**
 * Says how many companies the figures cover, from the report rather than from
 * the partner record: `member_partner_count` is the only thing on the wire that
 * distinguishes a rolled-up line from a direct one, and a heading that claimed
 * a scope the numbers were not computed under would be the worst kind of wrong.
 */
function rollupDescription(
  partner: Partner,
  scope: ReportScope,
  row: PartnerRollupRow | undefined,
): string {
  if (row === undefined) return `Pipeline, run rate and billing history for ${partner.name}.`;
  if (scope === "direct" || row.member_partner_count === 1) {
    return `Pipeline, run rate and billing history booked by ${partner.name} itself.`;
  }
  return `Pipeline, run rate and billing history across ${formatCount(row.member_partner_count)} companies — ${partner.name} and everything it owns.`;
}

/**
 * The term timeline only exists on the agreement DETAIL response, so each
 * agreement needs its own request. They are fetched together and each card
 * reports its own outcome — one failed agreement should not blank the others.
 */
function AgreementCards({
  agreements,
  canEdit,
  onAction,
}: {
  agreements: readonly Agreement[];
  canEdit: boolean;
  onAction: (action: PartnerScreenAction) => void;
}): ReactNode {
  const details = useAgreementDetails(agreements.map((agreement) => agreement.id));

  return (
    <>
      {agreements.map((agreement, index) => {
        const detail = details[index];
        return (
          <Card key={agreement.id}>
            <CardHeader
              title={
                agreement.is_default ? (
                  <span className={styles.cardTitle}>
                    {agreement.name}
                    <Badge shape="tag" tone="info">
                      Default
                    </Badge>
                  </span>
                ) : (
                  agreement.name
                )
              }
              description={
                <>
                  {agreement.notes ?? "Effective-dated split terms, newest first."}
                  {/* Which agreement is the default is not decoration: it is the
                      one every deal that pins nothing resolves through, here and
                      under every company this partner owns. */}
                  {agreement.is_default && (
                    <span className={styles.defaultNote}>
                      Deals that pin no agreement — this partner's, and those of every company it
                      owns — resolve their split here.
                    </span>
                  )}
                </>
              }
              {...(canEdit
                ? {
                    actions: (
                      <div className={styles.cardActions}>
                        <Button
                          variant="soft"
                          onClick={() => onAction({ kind: "create_term", agreement })}
                        >
                          <span aria-hidden="true">+</span>
                          Add term
                        </Button>
                        <Button
                          small
                          variant="subtle"
                          aria-label={`Edit ${agreement.name}`}
                          onClick={() => onAction({ kind: "edit_agreement", agreement })}
                        >
                          Edit
                        </Button>
                        <Button
                          small
                          variant="subtle"
                          aria-label={`Delete ${agreement.name}`}
                          onClick={() => onAction({ kind: "delete_agreement", agreement })}
                        >
                          Delete
                        </Button>
                      </div>
                    ),
                  }
                : {})}
            />

            {detail !== undefined && detail.data !== undefined ? (
              <AgreementTermsTimeline
                agreement={detail.data}
                {...(canEdit
                  ? {
                      onEditTerm: (term: AgreementTerm) =>
                        onAction({ kind: "edit_term", agreement, term }),
                      onDeleteTerm: (term: AgreementTerm) =>
                        onAction({ kind: "delete_term", agreement, term }),
                      onAddTier: (term: AgreementTermWithTiers) =>
                        onAction({ kind: "create_tier", term }),
                      onEditTier: (term: AgreementTermWithTiers, tier: AgreementImplFeeTier) =>
                        onAction({ kind: "edit_tier", term, tier }),
                      onDeleteTier: (term: AgreementTermWithTiers, tier: AgreementImplFeeTier) =>
                        onAction({ kind: "delete_tier", term, tier }),
                    }
                  : {})}
              />
            ) : (
              <div className={styles.panelBody}>
                {detail !== undefined && detail.error !== null ? (
                  <ProblemMessage error={detail.error} action="load this agreement's terms" />
                ) : (
                  <Spinner label="Loading the term timeline…" />
                )}
              </div>
            )}
          </Card>
        );
      })}
    </>
  );
}
